<template>
  <div
    class="tenant-identity__container"
    :class="{expanded}"
  >
    <div
      class="size-toggle__container"
      @click="expanded = !expanded"
    >
      <font-awesome-icon
        :class="{expanded}"
        :icon="expanded ? `compress` : `expand-arrows-alt`"
      />
    </div>
    <img
      v-lazy="imageForIdentity(tenant)"
      :class="`${primary ? 'active-tenant' : ''} cotenant-avatar${selectionIndicator ? '_selected' : ''}`"
      :title="tenant.person_name"
    >
    <div
      v-if="selectionIndicator"
      class="selection-indicator-dot"
    />
    <div
      v-if="tenant.role === 'tor'"
      class="tenant-of-record"
    >
      <font-awesome-icon
        icon="key"
        class="tenant-of-record__icon"
      />
    </div>
    <base-indicator
      v-if="badge && badge.text"
      :text="badge.text"
      :style="badge.style"
      class="text-badge"
    />
    <owal-indicator-confidence
      v-if="tenant.f1_score && canSeeF1Score"
      :confidence="tenant.f1_score"
      :confidence-threshold="0.8"
      :automatic-color="tenant.f1_score >= 0.8"
      class="confidence-indicator"
    />
    <div class="cotenant-identity__label">
      {{ titleCase(tenant.name || tenant.person_name) }}
    </div>
  </div>
</template>

<script>
import { titleCase } from '@/libraries/util/string'
import { imageForCredentialUUID, urlForIdentityId, urlForFrontendFaceId } from '@/libraries/api/faces'
import OwalIndicatorConfidence from '@/libraries/owal-ui/indicators/OwalIndicatorConfidence'
import { mapGetters } from 'vuex'
import { Service, Operation } from '@/libraries/permissions'
import BaseIndicator from '@/libraries/owal-ui/indicators/BaseIndicator'

export default {
  name: 'BaseTenant',
  props: {
    /** @prop {Object} tenant The tenant displayed by this component. */
    tenant: {
      type: Object,
    },
    /** @prop {Boolean} selectionIndicator Determines if the profile should show the selection indicator. */
    selectionIndicator: {
      type: Boolean,
    },
    /** @prop {Object} primary Determines if the profile should have a darkened background. */
    primary: {
      type: Boolean,
    },
    /** @prop {Object} badge A string to show in the bottom right badge. */
    badge: {
      type: Object,
      default: () => {
        return {
          style: {
            color: '#000',
            background: 'rgb(0, 128, 0)',
            'font-weight': '750',
          },
          text: '',
        }
      },
    },
  },
  data () {
    return {
      /** Toggles the scaling of the component. */
      expanded: false,
    }
  },
  computed: {
    ...mapGetters({
      canPerformOperationForService: 'entitlements/canPerformOperationForService',
    }),
    /** @property {Boolean} canSeeF1Score Checks if the user is allowed to see the F1 score badge on the avatat.
     */
    canSeeF1Score () {
      return this.canPerformOperationForService(Service.FACES, Operation.VIEW_F1_SCORE)
    },
  },
  methods: {
    titleCase,
    imageForCredentialUUID,
    /**
     * @param {Object} identity The identity to return the image for.
     */
    imageForIdentity (identity) {
      if (identity.identityId) {
        const url = urlForIdentityId(identity.identityId, true)
        return url
      } else if (identity.frontend_face_uuid) {
        const url = urlForFrontendFaceId(identity.frontend_face_uuid, true)
        return url
      } else if (identity && identity.images && identity.images.length > 0) {
        // this block is a hack until faces & identities are merged.
        return identity.images[0].thumbnail_uri
      } else return '../static/OSquareNoPadding.png'
    },
  },
  components: {
    OwalIndicatorConfidence,
    BaseIndicator,
  },
}
</script>

<style scoped>
/** The tenant container */
.tentant-avatar__container {
  border-radius: 0.5rem;
  background-color: #eff2f7;
  position: relative;
  z-index: 2;
  height: 7.1428571429rem;
  width: 7.1428571429rem;
  border-radius: 50%;
  margin: 0;
  padding: 0;
  display: flex;
  align-content: center;
  justify-content: center;
}

/** The avatar's image. */
.tenant-avatar__container {
  display: inline-block;
}

/** The tenant's face image. */
.tenant-avatar__image,
.tenant-avatar__image_selected {
  display: inline-block;
  background-color: #eff2f7;
  object-fit: cover;
  height: 7.1428571429rem;
  width: 7.1428571429rem;
  border-radius: 50%;
  margin: 0;
  padding: 0;
  z-index: 9;
}

/** A container for each individual cotenant. */
.tenant-identity__container {
  display: flex;
  flex-direction: column;
  flex: 1;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  white-space: nowrap;
  padding: 0.5rem;
  transition: all 300ms ease,
    background-color 350ms cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

/** Round corners on first and last tenant - horizontal layout. */
.owal-cotenants__container_horizontal
  .tenant-identity__container:first-of-type {
  border-top-left-radius: 1rem;
  border-bottom-left-radius: 1rem;
}

.owal-cotenants__container_horizontal .tenant-identity__container:last-of-type {
  border-top-right-radius: 1rem;
  border-bottom-right-radius: 1rem;
}

/** Round corners on leading and trailing - vertical layout. */
.owal-cotenants__container_vertical .tenant-identity__container:first-of-type {
  border-top-left-radius: 1rem;
  border-top-right-radius: 1rem;
}

.owal-cotenants__container_vertical .tenant-identity__container:last-of-type {
  border-bottom-left-radius: 1rem;
  border-bottom-right-radius: 1rem;
}

/** Highlighting on hover. */
.tenant-identity__container:hover {
  background: #00000033;
}

/** The name of each cotenant. */
.tenant-identity__container .cotenant-identity__label,
.tenant-identity__container .active-tenant.cotenant-identity__label {
  display: block;
  border-radius: 0.25rem;
  margin-top: 4rem;
  padding: 0.15rem;
  opacity: 0;
  position: absolute;
  color: #475669;
  transition: all 300ms ease;
  transform: translateY(-1rem);
}

/** The active tenant needs a slightly larger margin above the label. */
.tenant-identity__container .active-tenant + .cotenant-identity__label {
  margin-top: 4.5rem;
}

/** On hover,fade the name label in. */
.tenant-identity__container:hover .cotenant-identity__label {
  transform: translateY(0);
  opacity: 1;
}

/** Cotenant avatar images. */
.cotenant-avatar,
.cotenant-avatar_selected {
  display: inline-flex;
  flex-direction: column;
  background-color: #eff2f7;
  object-fit: cover;
  align-items: center;
  justify-content: center;
  align-content: center;
  flex-grow: 0;
  height: 5rem;
  width: 5rem;
  margin: 0.25rem 0.25rem 0 0.25rem;
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-radius: 50%;
  transition: transform 350ms cubic-bezier(0.175, 0.885, 0.32, 1.275),
    z-index 200ms ease,
    border-width 325ms cubic-bezier(0.175, 0.885, 0.32, 1.275),
    border-color 325ms cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.active-tenant,
.tenant-identity__container:hover .active-tenant {
  transform: scale(1.5);
  margin-left: 1rem;
  margin-right: 1rem;
  transition: margin 300ms cubic-bezier(0.175, 0.885, 0.32, 1.275),
    transform 300ms cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

/** Transform the profile buttons on hover. */
.tenant-identity__container:hover .cotenant-avatar:not(.active-tenant) {
  transform: scale(1.1);
  z-index: 12;
  border-color: #eff2f7;
  border-width: 2px;
  margin: 0.25rem 0.25rem 0 0.25rem;
}

/** Modify avatars on selection. */
.cotenant-avatar_selected {
  background: #7fdbff;
  box-shadow: 0 0 1px 1px #7fdbff;
  transform: translateY(-0.5rem);
}

/** The text badge. */
.text-badge {
  position: absolute;

  display: flex;
  flex-direction: row;
  height: 1.75rem;
  width: 1.75rem;
  align-content: center;
  align-items: center;
  justify-content: center;
  align-self: flex-end;
  bottom: 0;
  border: 0;
  border-radius: 50%;

  transition: all 300ms cubic-bezier(0.175, 0.885, 0.32, 1.275);
  z-index: 80;
  padding: 0 0.5rem;
  box-sizing: border-box;
  overflow: hidden;
  animation: burst 150ms ease-out 0ms 1;
}

/** If there's another badge before the text badge, show a hint of overlap. */
.tenant-of-record + .text-badge {
  transform: translateX(-0.75rem);
}

@keyframes burst {
  0% {
    opacity: 0;
    transform: scale(0.1);
  }

  90% {
    opacity: 1;
    transform: scale(1.3);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

/** An icon indicating that a tenant is the TOR. */
.tenant-of-record {
  position: absolute;

  display: flex;
  flex-direction: row;
  height: 1.75rem;
  width: 1.75rem;
  align-content: center;
  align-items: center;
  justify-content: center;
  align-self: flex-end;
  bottom: 0;

  background: #eff2f7;
  border-radius: 0.875rem;
  opacity: 0.75;

  transition: all 300ms ease-out,
    transform 300ms cubic-bezier(0.175, 0.885, 0.32, 1.275);
  z-index: 80;
  padding: 0 0.5rem;
  box-sizing: border-box;
}

.tenant-of-record__icon {
  padding: 0.375rem;
  height: 1.75rem;
  width: 1.75rem;
  border-radius: 50%;
  transition: box-shadow 100ms ease;
  z-index: 101;
}

.tenant-of-record:hover .tenant-of-record__icon {
  box-shadow: 0 0 2px 2px rgba(71, 86, 105, 0.15);
}

/** Tenant Of Record Hover */
.tenant-of-record:hover {
  opacity: 1;
  width: fit-content;
  justify-content: flex-start;
  padding: 0 0.5rem 0 0;
  z-index: 100;
  align-self: center;
  box-shadow: 0 0 2px 2px rgba(71, 86, 105, 0.15);
}

.tenant-identity__container .tenant-of-record::after {
  content: 'Tenant of Record';
  display: flex;
  opacity: 0;
  width: 0;
  height: 1.75rem;
  transition: opacity 200ms ease, width 200ms ease;
  background: #eff2f7;
  border-top-right-radius: 0.875rem;
  border-bottom-right-radius: 0.875rem;
  justify-content: center;
  align-content: center;
  align-items: center;
  z-index: 75;
}

.tenant-of-record:hover::after {
  opacity: 1;
  width: 8rem;
  opacity: 1;
  z-index: 75;
}

/** The confidence indicator */
.confidence-indicator {
  z-index: 90;
  align-self: flex-end;
  justify-self: flex-end;
  position: relative;
  margin: -0.875rem 0;
}

/** Selection indicator */
.selection-indicator-dot {
  display: block;
  position: relative;
  height: 0.45rem;
  width: 0.45rem;
  margin: -0.5rem auto 0 auto;
  border-radius: 50%;
  background: #7fdbff;
  border: 1px solid #7fdbff99;
  transition: all 300ms ease;
  transform: translateY(0.2rem);
  animation: appear 150ms ease-out 0ms 1;
}

/** Animate the blue dot ("selection indicator") in. */
@keyframes appear {
  0% {
    opacity: 0;
    transform: translateY(0.5rem);
  }

  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

/** Adjust indicators if the tenant is showing selected indicator. */
.selection-indicator-dot ~ .tenant-of-record,
.selection-indicator-dot ~ .text-badge {
  transform: translateY(-0.5rem);
}

.selection-indicator-dot ~ .tenant-of-record + .text-badge {
  transform: translateX(-0.75rem) translateY(-0.5rem);
}

/** Expansion Toggle */

.size-toggle__container {
  height: 2rem;
  width: 2rem;
  border-radius: 1rem;
  background: black;
  color: white;
  top: 0;
  align-self: flex-end;
  justify-self: flex-start;
  z-index: 100;
  display: flex;
  align-content: center;
  align-items: center;
  justify-content: center;
  position: absolute;

}

.size-toggle__container {
  transition: transform 300ms ease;
  transform: translateY(-0.5rem) translateX(0);
}

.expanded > .size-toggle__container  {
  transform: translateY(-2rem) translateX(2rem);
}

.size-toggle__container font-awesome-icon {
  height: 100%;
  width: 100%;
  transform: initial;
}

.tenant-identity__container .size-toggle__container {
  opacity: 0;
  transition: all 300ms ease;
}

.tenant-identity__container:hover .size-toggle__container,
.expanded .size-toggle__container {
  opacity: 1;
}

.expanded > img {
  transform: scale(2.5) !important;
  z-index: 99;
}

</style>
